<template>
  <div class="atb-divide-y atb-divide-gray-300 atb-py-6">
    <div class="atb-pb-4">
      <label class="atb-ml-20">
        <span class="card-title"> {{ shortName }} </span>
        <span v-if="readOnly">
          <strong> is locked by another user </strong>
        </span>
      </label>
    </div>
    <Card class="card-size atb-ml-20 atb-pt-6">
      <template #body>
        <Form>
          <div
            class="atb-grid atb-grid-cols-2 atb-gap-y-4 atb-gap-x-14 atb-px-2 atb-py-4"
          >
            <div
              :class="{ 'atb-flex atb-items-center': shortNameErrorMsg.length }"
            >
              <FormField
                :label="$t('shortName')"
                required="true"
                form-field-class="input-field-width"
                :message="shortNameErrorMsg"
                :message-type="'error'"
              >
                <Textbox
                  id="overviewTabShortNameTextbox"
                  v-model.trim="overviewData.shortName"
                  type="text"
                  :maxlength="MaxValue.MAX_SHORT_NAME"
                  :state="
                    !isDuplicateShortName || shortNameErrorMsg
                      ? 'error'
                      : 'basic'
                  "
                  @update:modelValue="validateShortName"
                  @blur="validateDuplicateShortName"
                  :disabled="readOnly"
                />
              </FormField>
              <span class="atb-pl-2">{{ remShortNameCount }}</span>
            </div>
            <div>
              <FormField
                id="overviewTabFormatDropdown"
                :label="$t('format')"
                required="true"
                form-field-class="input-field-width"
              >
                <Select
                  v-model="overviewData.format"
                  :options="[
                    { name: $t('alphanumeric') },
                    { name: $t('boolean') },
                    { name: $t('date') },
                    { name: $t('number') },
                  ]"
                  :disabled="readOnly"
                >
                  <template v-slot:option="{ item }">
                    {{ item.name }}
                  </template>
                </Select>
              </FormField>
            </div>
            <div
              :class="{ 'atb-flex atb-items-center': longNameErrorMsg.length }"
            >
              <FormField
                :label="$t('longName')"
                required="true"
                form-field-class="input-field-width"
                :message="longNameErrorMsg"
                :message-type="'error'"
              >
                <Textbox
                  id="overviewTabLongNameTextbox"
                  v-model.trim="overviewData.longName"
                  :maxlength="MaxValue.MAX_LONG_NAME"
                  :state="longNameErrorMsg.length ? 'error' : 'basic'"
                  :disabled="readOnly"
                />
              </FormField>
              <span class="atb-pl-2">{{ remLongNameCount }}</span>
            </div>
            <div
              :class="{ 'atb-flex atb-items-center': lengthErrorMsg.length }"
            >
              <FormField
                required="true"
                :label="$t('length')"
                form-field-class="input-field-width"
                :message="lengthErrorMsg"
                :message-type="'error'"
              >
                <Textbox
                  id="overviewTabLengthTextbox"
                  v-model.number="overviewData.length"
                  oninput="value=value.slice(0,maxLength)"
                  :input-type="'number'"
                  :min="1"
                  :maxlength="MaxValue.MAX_LENGTH"
                  :state="lengthErrorMsg.length ? 'error' : 'basic'"
                  :disabled="readOnly"
                  @keypress="validateLength($event)"
                />
              </FormField>
              <span class="atb-pl-2">{{ remLengthCount }}</span>
            </div>
            <div class="atb-row-span-2">
              <FormField
                :label="$t('description')"
                form-field-class="input-field-width"
              >
                <Textarea
                  id="overviewTabDescriptionTextbox"
                  v-model="overviewData.description"
                  field-control-class="atb-h-24"
                  :disabled="readOnly"
                />
              </FormField>
            </div>
            <div>
              <FormField
                :label="$t('initialValue')"
                form-field-class="input-field-width"
              >
                <Textbox
                  id="overviewTabInitialValueTextbox"
                  v-model="overviewData.initializationValue"
                  :maxlength="MaxValue.MAX_INITIAL_VALUE"
                  :disabled="readOnly"
                />
              </FormField>
              <span class="atb-pl-2">{{ remInitialValueCount }}</span>
            </div>
            <div>
              <FormField
                :label="$t('defaultValue')"
                form-field-class="input-field-width"
              >
                <Textbox
                  id="overviewTabDefaultValueTextbox"
                  v-model="overviewData.defaultValue"
                  :maxlength="MaxValue.MAX_DEFAULT_VALUE"
                  :disabled="readOnly"
                />
              </FormField>
              <span class="atb-pl-2">{{ remDefaultValueCount }}</span>
            </div>
          </div>
          <div class="atb-pb-4">
            <Checkbox
              id="overviewTabExperianProtectedCheckbox"
              disabled
              :label="$t('experianProtected')"
              name="experianProtectedCheckbox"
              v-model:checked="overviewData.isStagg"
            />
          </div>
        </Form>
        <div
          id="overviewTabSystemsGrid"
          v-show="useSystems"
          class="eds-table-wrap atb-w-full atb-h-48"
        >
          <AgGridVue
            class="ag-theme-eds atb-w-full atb-h-full"
            :columnDefs="columnDefs"
            :rowData="clientSystems.content"
            :pagination="false"
            :row-selection="'multiple'"
            :row-multi-select-with-click="true"
            :suppressPaginationPanel="true"
            :suppressCellSelection="true"
            :suppressRowClickSelection="true"
            @grid-ready="onGridReady"
            :on-row-selected="onRowSelected"
          />
        </div>
        <div class="atb-flex atb-flex-row atb-justify-end atb-gap-x-8 atb-pt-8">
          <Button
            id="overviewTabDeleteButton"
            type="submit"
            motif="basic"
            :label="$t('delete')"
            :title="$t('delete')"
            :disabled="readOnly || isDeleteDisabled"
            @click="closeDeleteModal"
          />
          <Button
            id="overviewTabSaveButton"
            type="submit"
            motif="primary"
            :label="$t('save')"
            :title="$t('save')"
            :disabled="readOnly || isSaveDisabled || !isValidShortName"
            @click="saveOverviewDetails"
          />
        </div>
      </template>
    </Card>
  </div>
  <Modal
    :show="isModalOpen"
    container-class="atb-w-96"
    body-class="atb-border-none"
    footer-class="atb-flex atb-flex-row atb-justify-end atb-gap-8"
    @onModalClose="closeDeleteModal"
  >
    <template #header> {{ $t('areYouSure') }} </template>
    <template #body>
      <div
        id="overviewTabDeleteConfirmationModal"
        class="atb-flex atb-flex-col atb-gap-4"
      >
        {{ messages.checkingDeletion }}
      </div>
    </template>
    <template #footer>
      <Button
        motif="basic"
        @click="closeDeleteModal"
      >
        {{ $t('cancel') }}
      </Button>
      <Button
        id="overviewTabDeleteConfirmationButton"
        @click="deleteStatus"
      >
        {{ $t('ok') }}</Button
      >
    </template>
  </Modal>
  <Modal
    :show="isDeleteErrorDisplayed"
    container-class="atb-w-96"
    body-class="atb-border-none"
    footer-class="atb-flex atb-flex-row atb-justify-end atb-gap-8"
    @onModalClose="closeDeleteErrorModal"
  >
    <template #header> {{ $t('oops') }} </template>
    <template #body>
      <div class="atb-flex atb-flex-col atb-gap-4">
        {{ $t('unknownError') }}
      </div>
    </template>
    <template #footer>
      <Button @click="closeDeleteErrorModal">{{ $t('ok') }}</Button>
    </template>
  </Modal>
  <Modal
    :show="isSaveConfirmationDisplayed"
    container-class="atb-w-96"
    body-class="atb-border-none"
    footer-class="atb-flex atb-flex-row atb-justify-end atb-gap-8"
    @onModalClose="isSaveConfirmationDisplayed = false"
  >
    <template #header>{{ $t('areYouSure') }}</template>
    <template #body>
      <div
        id="overviewTabSaveConfirmationModal"
        class="atb-flex atb-flex-col atb-gap-4"
      >
        {{ messages.checkingSave }}
      </div>
    </template>
    <template #footer>
      <Button
        motif="basic"
        @click="isSaveConfirmationDisplayed = false"
        >{{ $t('cancel') }}</Button
      >
      <Button
        id="overviewTabSaveConfirmationButton"
        @click="saveConfirmation"
        >{{ $t('ok') }}</Button
      >
    </template>
  </Modal>
  <Modal
    :show="isSaveErrorDisplayed"
    container-class="atb-w-96"
    body-class="atb-border-none"
    footer-class="atb-flex atb-flex-row atb-justify-end atb-gap-8"
    @onModalClose="isSaveErrorDisplayed = false"
  >
    <template #header>{{ $t('oops') }}</template>
    <template #body>
      <div class="atb-flex atb-flex-col atb-gap-4">
        {{ saveError }}
      </div>
    </template>
    <template #footer>
      <Button @click="isSaveErrorDisplayed = false">{{ $t('ok') }}</Button>
    </template>
  </Modal>
</template>

<script lang="ts" setup>
  import { useClientStore } from '@/app/features/ams/client/client-store';
  import { useSystemsStore } from '@/app/features/ams/systems/systems-store';
  import { MaxValue } from '@/app/shared/enums/max-value.enum';
  import { Motif } from '@/app/shared/enums/motif-enum';
  import { useModal } from '@/app/shared/hooks/use-modal';
  import { useAlertStore } from '@/app/shared/stores/alert-store';
  import { CodeMetaInformationDataEvent } from '@/app/shared/types/events/code-meta-information-data-event.interface';
  import { OverviewDataEvent } from '@/app/shared/types/events/overview-data-event.interface';
  import { Messages } from '@/app/shared/types/messages.interface';
  import { System } from '@/app/shared/types/system.interface';
  import {
    validateAlphanumeric,
    validateEmpty,
    validateLuaKeyWord,
    validateNumber,
  } from '@/app/shared/utils/validation.util';
  import { ENTITY_ALPHANUMERIC_REGEX } from '@/app/utils/regexpr-const';
  import Button from '@atb-ui/eds-vue/src/components/Button.vue';
  import Card from '@atb-ui/eds-vue/src/components/Card.vue';
  import Checkbox from '@atb-ui/eds-vue/src/components/Checkbox.vue';
  import Form from '@atb-ui/eds-vue/src/components/Form/Form.vue';
  import FormField from '@atb-ui/eds-vue/src/components/Form/FormField.vue';
  import Modal from '@atb-ui/eds-vue/src/components/Modal.vue';
  import Select from '@atb-ui/eds-vue/src/components/Select.vue';
  import Textarea from '@atb-ui/eds-vue/src/components/Textarea.vue';
  import Textbox from '@atb-ui/eds-vue/src/components/Textbox.vue';
  import { AgGridEvent, GridApi } from 'ag-grid-community';
  import { AgGridVue } from 'ag-grid-vue3/lib/AgGridVue';
  import { storeToRefs } from 'pinia';
  import { computed, reactive, Ref, ref, toRefs, watch, onMounted } from 'vue';
  import { useI18n } from 'vue-i18n';

  interface OverviewTabEvent {
    (_event: 'codeMetaInformationsDeleted'): void;
    (_event: 'saveOverviewData', _params: OverviewDataEvent): void;
    (
      _event: 'setDirtyStatus',
      stringField: string,
      booleanField: boolean
    ): void;
  }

  interface OverviewTabProps {
    useSystems?: boolean;
    codeType: string;
    messages: Messages;
    codeMetaInformationData: CodeMetaInformationDataEvent;
    readOnly: boolean;
    checkDuplicateShortName: any;
  }

  const emit = defineEmits<OverviewTabEvent>();
  const props = withDefaults(defineProps<OverviewTabProps>(), {
    useSystems: false,
    readOnly: false,
  });
  const { t } = useI18n();
  const clientStore = useClientStore();
  const systemsStore = useSystemsStore();
  const alertStore = useAlertStore();
  const { isModalOpen, changeModalVisibility } = useModal();
  const { clientSystems } = storeToRefs(systemsStore);

  const {
    useSystems,
    codeType,
    messages,
    codeMetaInformationData,
    readOnly,
    checkDuplicateShortName,
  } = toRefs(props);

  const overviewData = codeMetaInformationData.value.OverviewDataEvent;
  const clientId = computed(() => clientStore.getSelectedClient.id);
  let gridApi = reactive({}) as Ref<GridApi | null>;

  const columnDefs = [
    {
      field: 'name',
      headerName: '',
      width: 56,
      suppressSizeToFit: true,
      headerCheckboxSelection: true,
      checkboxSelection: true,
    },
    {
      field: 'systemName',
      headerName: t('systemName'),
      suppressSizeToFit: false,
    },
  ];

  let manualRowSelectionCount = 0;
  const onGridReady = async ({ api }: AgGridEvent) => {
    await systemsStore.fetchAllSystemsByClient(clientId.value);
    gridApi.value = api;
    updateSelectedElements();
    gridApi.value.sizeColumnsToFit();
  };

  const updateSelectedElements = () => {
    const selectedSystemsIds =
      overviewData.systems?.map((elem: System) => elem.id) || [];
    selectedSystemsIds.length &&
      gridApi.value?.forEachNode((node) => {
        node.setSelected(selectedSystemsIds.includes(node.data.id));
      });
    manualRowSelectionCount = selectedSystemsIds.length;
  };

  let selectedNodes = reactive([]) as any[];
  const isSaveConfirmationDisplayed = ref(false);
  const isSaveErrorDisplayed = ref(false);
  const isDeleteErrorDisplayed = ref(false);

  const shortNameErrorMsg = ref('');
  const longNameErrorMsg = ref('');
  const lengthErrorMsg = ref('');
  const saveError = ref(t('unknownError'));
  const isDuplicateShortName = ref(true);
  const isValidShortName = ref(true);
  const initialShortName = ref('');

  const shortName = computed((): string => {
    return overviewData.shortName;
  });

  const compareShortNames = computed(() => {
    if (initialShortName?.value && overviewData?.shortName) {
      return initialShortName.value === overviewData.shortName;
    }
    return false;
  });

  const remShortNameCount = computed((): number => {
    return MaxValue.MAX_SHORT_NAME - overviewData.shortName?.length;
  });

  const remLongNameCount = computed((): number => {
    return MaxValue.MAX_LONG_NAME - overviewData.longName?.length;
  });

  const remLengthCount = computed((): number => {
    const lengthValue = overviewData.length?.toString();
    return lengthValue
      ? MaxValue.MAX_LENGTH - lengthValue?.length
      : MaxValue.MAX_LENGTH;
  });

  const remInitialValueCount = computed((): number => {
    return overviewData.initializationValue
      ? MaxValue.MAX_INITIAL_VALUE - overviewData.initializationValue?.length
      : MaxValue.MAX_INITIAL_VALUE;
  });

  const remDefaultValueCount = computed((): number => {
    return overviewData.defaultValue
      ? MaxValue.MAX_DEFAULT_VALUE - overviewData.defaultValue?.length
      : MaxValue.MAX_DEFAULT_VALUE;
  });

  const isSaveDisabled = computed((): boolean => {
    return !dirtyStatus.value && !newStatus.value;
  });

  const isDeleteDisabled = computed((): boolean => {
    return newStatus.value;
  });

  const dirtyStatus = computed(() => {
    return codeMetaInformationData.value?.isDirty;
  });

  const newStatus = computed(() => {
    return codeMetaInformationData.value?.isNew;
  });

  watch(
    overviewData,
    () => {
      setDirtyField(true);
    },
    { deep: true }
  );

  const onRowSelected = () => {
    const selectedData = gridApi.value?.getSelectedNodes();
    selectedNodes = selectedData?.map((node) => node.data) || [];
    manualRowSelectionCount > 0
      ? manualRowSelectionCount--
      : setDirtyField(true);
  };

  const setDirtyField = (isDirty: boolean) => {
    codeMetaInformationData.value.isDirty = isDirty;
    emit('setDirtyStatus', t('overview'), isDirty);
  };

  const validateRequiredFields = (): boolean => {
    const { longName, length } = overviewData;
    const errMsg = t('emptyField');
    let isReqFieldError = false;
    if (!shortName.value.length) {
      shortNameErrorMsg.value = errMsg;
      isReqFieldError = true;
    } else {
      shortNameErrorMsg.value = '';
    }
    if (!longName.length) {
      longNameErrorMsg.value = errMsg;
      isReqFieldError = true;
    } else {
      longNameErrorMsg.value = '';
    }
    if (!length && length <= 0) {
      lengthErrorMsg.value = errMsg;
      isReqFieldError = true;
    } else {
      lengthErrorMsg.value = '';
    }
    return isReqFieldError;
  };

  const validateShortName = async () => {
    const { shortName } = overviewData;
    const isAlphanumeric = validateAlphanumeric(
      ENTITY_ALPHANUMERIC_REGEX,
      shortName
    );
    const isEmpty = validateEmpty(shortName);
    const isLuaKeyword = validateLuaKeyWord(shortName);
    isValidShortName.value = true;
    shortNameErrorMsg.value = '';
    if (!isAlphanumeric || isEmpty || !isLuaKeyword) {
      isValidShortName.value = false;
      shortNameErrorMsg.value = t('shortNameError');
      alertStore.showNotification(
        !isAlphanumeric
          ? messages.value.nameInvalid
          : t('reservedKeywordsError'),
        codeType.value,
        Motif.ERROR
      );
    }
  };

  const validateDuplicateShortName = async () => {
    if (
      !compareShortNames.value &&
      isValidShortName &&
      overviewData.shortName?.length > 0
    ) {
      isDuplicateShortName.value = await checkDuplicateShortName.value(
        overviewData.shortName
      );
    }
  };

  const validateLength = ($event: KeyboardEvent) => {
    const isNumber = validateNumber($event.key);
    if (!isNumber) $event.preventDefault();
  };

  const saveOverviewDetails = async () => {
    if (validateRequiredFields()) {
      isSaveConfirmationDisplayed.value = false;
      return;
    }
    if (!compareShortNames.value) {
      isDuplicateShortName.value = await checkDuplicateShortName.value(overviewData.shortName);
      if (!isDuplicateShortName.value) {
        alertStore.showNotification(t('duplicateShortName'), codeType.value, Motif.ERROR);
        return;
      }
    }
    isSaveConfirmationDisplayed.value = true;
  };

  const saveOverview = async () => {
    const overviewDataObj = useSystems
      ? {
          ...overviewData,
          systems: [...selectedNodes],
        }
      : {
          ...overviewData,
        };
    setDirtyField(false);
    emit('saveOverviewData', overviewDataObj);
    clientStore.fetchAttributeFilterFunctionList(clientId.value);
  };

  const saveConfirmation = () => {
    isSaveConfirmationDisplayed.value = false;
    saveOverview();
  };

  const closeDeleteModal = () => {
    changeModalVisibility();
  };

  const deleteStatus = async () => {
    changeModalVisibility();
    emit('codeMetaInformationsDeleted');
  };

  const closeDeleteErrorModal = () => {
    isDeleteErrorDisplayed.value = false;
  };

  onMounted(() => {
    updateSelectedElements();
    gridApi.value?.sizeColumnsToFit();
    initialShortName.value = overviewData.shortName;
  });
</script>

<style scoped>
  .card-size {
    width: 70%;
  }

  .input-field-width {
    width: 90%;
  }

  .option-class {
    text-align: left;
    padding: 10px 16px;
  }

  .d-active {
    background-color: #426da9;
    color: white;
  }

  .card-title {
    font-weight: 500;
    font-size: 16px;
    color: #767676;
  }
</style>
